/**
 * Working-day weekday bitmask helpers.
 *
 * The mask uses bit 0 = Monday … bit 6 = Sunday so that the common European
 * working week (Monday–Friday) is the contiguous low five bits (binary
 * `0011111` = 31). This is the source of truth for which calendar days are
 * "workdays" for a time-tracking period and therefore which past days incur an
 * expected-hours flexitime deficit when no booking exists.
 */

/** Default mask: Monday–Friday (bits 0–4 set). */
export const DEFAULT_WORKDAY_MASK = 31;

/** Mask with every weekday set (Monday–Sunday). */
export const ALL_WORKDAYS_MASK = 127;

/** Ordered weekday keys matching bit positions 0–6 (Monday-first). */
export const WORKDAY_MASK_KEYS = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'] as const;

export type WorkdayKey = (typeof WORKDAY_MASK_KEYS)[number];

/**
 * Convert the JavaScript `Date.getUTCDay()` value (0 = Sunday … 6 = Saturday)
 * to a Monday-first bit index (0 = Monday … 6 = Sunday).
 */
function toMondayFirstBit(jsDay: number): number {
  return (jsDay + 6) % 7;
}

/**
 * Whether the given Monday-first bit index (0 = Monday … 6 = Sunday) is set in
 * the mask.
 */
export function isWorkdayBitSet(mask: number, mondayFirstBit: number): boolean {
  return (mask & (1 << mondayFirstBit)) !== 0;
}

/**
 * Whether the calendar date (interpreted in UTC) falls on a working weekday
 * according to the mask. Does not consider holidays or absences — only the
 * weekday pattern.
 *
 * @param isoDate calendar date as `YYYY-MM-DD`
 */
export function isWorkday(mask: number, isoDate: string): boolean {
  const parts = isoDate.split('-').map(Number);
  const year = parts[0] ?? 1970;
  const month = parts[1] ?? 1;
  const day = parts[2] ?? 1;
  const jsDay = new Date(Date.UTC(year, month - 1, day)).getUTCDay();
  return isWorkdayBitSet(mask, toMondayFirstBit(jsDay));
}

/** Build a mask from a set of Monday-first weekday keys. */
export function buildWorkdayMask(keys: Iterable<WorkdayKey>): number {
  let mask = 0;
  for (const key of keys) {
    const bit = WORKDAY_MASK_KEYS.indexOf(key);
    if (bit >= 0) mask |= 1 << bit;
  }
  return mask;
}

/** Expand a mask into the set of enabled Monday-first weekday keys. */
export function workdayKeysFromMask(mask: number): WorkdayKey[] {
  return WORKDAY_MASK_KEYS.filter((_key, bit) => isWorkdayBitSet(mask, bit));
}

/**
 * Convert a workday bitmask into an array of ISO weekday indices, Monday-first
 * (`0 = Monday … 6 = Sunday`). The bit index equals the ISO index, so this is
 * the interchange format used by the shared `zag-weekday-picker` and by
 * strategy `workingDays`.
 */
export function workdayMaskToIsoDays(mask: number): number[] {
  const days: number[] = [];
  for (let bit = 0; bit < WORKDAY_MASK_KEYS.length; bit++) {
    if (isWorkdayBitSet(mask, bit)) days.push(bit);
  }
  return days;
}

/**
 * Convert an array of Monday-first ISO weekday indices (`0 = Monday … 6 =
 * Sunday`) into a workday bitmask. Indices outside 0–6 are ignored.
 */
export function isoDaysToWorkdayMask(days: Iterable<number>): number {
  let mask = 0;
  for (const day of days) {
    if (Number.isInteger(day) && day >= 0 && day < WORKDAY_MASK_KEYS.length) {
      mask |= 1 << day;
    }
  }
  return mask;
}

/** Number of working days in the mask (popcount of the low 7 bits). */
export function countWorkdays(mask: number): number {
  let count = 0;
  for (let bit = 0; bit < WORKDAY_MASK_KEYS.length; bit++) {
    if (isWorkdayBitSet(mask, bit)) count++;
  }
  return count;
}

/** Clamp an arbitrary number into a valid 7-bit workday mask (0–127). */
export function normalizeWorkdayMask(value: number | null | undefined): number {
  if (value == null || !Number.isFinite(value)) return DEFAULT_WORKDAY_MASK;
  const intValue = Math.trunc(value);
  if (intValue < 0) return 0;
  if (intValue > ALL_WORKDAYS_MASK) return ALL_WORKDAYS_MASK;
  return intValue;
}
